import {
  DayOfWeek,
  LocalDate,
  TemporalAdjusters,
  ZonedDateTime,
  ZoneId,
} from '@js-joda/core';
import { DemoUserInput } from '@teamexos/test-data-service';
// CoachTouchPointType is used by DemoUserInput.dailyActionConfig.type but is not
// re-exported from the package root, so it must be imported from its internal path.
import { CoachTouchPointType } from '@teamexos/test-data-service/dist/cjs/graphql/types';

import {
  CoachSessionType,
  MemberExperience,
  PlanItemType,
  UserGoalType,
} from '../../../../graphql/types';

export interface AdminDemoUserInput extends DemoUserInput {
  localId: string;
  /**
   * Public path (served from `public/`) of the avatar image to upload and
   * assign after the member is created. The image is uploaded via the standard
   * signed-url flow and set with the `AdminUpdateUser` mutation.
   * `createDemoUser` ignores this field.
   */
  avatarImagePath?: string;
}

export const getCoachTrainingMembers = ({
  coachId,
  nutritionistId,
}: {
  coachId: string;
  nutritionistId?: string;
}): AdminDemoUserInput[] => {
  const now = ZonedDateTime.now(ZoneId.UTC);

  return [
    {
      localId: 'AshleyPhillip',
      firstName: 'Ashley',
      lastName: 'Phillip',
      createdAt: ZonedDateTime.parse('2022-07-01T00:00:00.0Z'),
      coachId,
      pronoun: 'She / Her',
      nutritionistId,
      lastAppActivity: ZonedDateTime.now(ZoneId.UTC),
      shouldStarUser: true,
      shouldCreateHistoricalData: true,
      shouldAssignPractice: true,
      shouldCreateSessions: true,
      shouldSendMessage: true,
      videos: [
        '6f453678-2742-4975-a830-d73156d4f5d2',
        'a5c667e5-2d4a-4ff1-bcef-6902c8a1cc17',
      ],
      dailyActionConfig: {
        type: CoachTouchPointType.OneWeekPostSessionCheckIn,
        due: now,
      },
      onboardingSurveys: {
        birthday: LocalDate.now().minusYears(38).toString(),
        height: 5.5,
        weight: 128,
        identity: 1,
        goals: 0,
        motivation: 3,
        barriers: [0, 2, 6],
        passions: [18, 12, 15],
        experience: 2,
        movement1: 3,
        movement2: 4,
        nutrition1: 2,
        nutrition2: 4,
        recovery1: 2,
        recovery2: 1,
        recovery3: 3,
        mindset1: 3,
        mindset2: 3,
        mindset3: 2,
      },
      note: {
        coachId,
        title: 'Coaching Session Note',
        content: `<p>Mid-Level Manager. Works hybrid schedule. Travels 2-3x/month to visit different sites. Has a 5-year old daughter, Sophia.&nbsp;</p><p></p><p><strong>Goal</strong></p><ul><li><p>Avid runner since college. Competes in a marathon each year</p></li><li><p>Next marathon is 4 months away. Looking for a new PR <strong>(under 4 hrs)</strong></p></li></ul><p></p><p><strong>Typical run schedule:&nbsp;</strong></p><ul><li><p>4x/week</p></li><li><p>1 track day, 2 medium distance days, 1 long day</p></li></ul><p></p><p>*Felt fatigued lately. Having trouble going to sleep and sometimes wakes up in middle of the night.&nbsp;</p><p>*Right knee pain during her running lately</p><p>*Has never hired a trainer or endurance coach. Wondering if we can help her set a PR</p><p></p><p><strong>&nbsp;Typical day of eating:&nbsp;</strong></p><ul><li><p>Breakfast: 2 eggs, ½ cup oatmeal, fresh berries, coffee, water</p></li><li><p>Snack AM: greek yogurt, granola, and/or protein shake smoothie</p></li><li><p>Lunch: Spinach salad with grilled chicken, vegetable variety, avocado oil,water</p></li><li><p>Snack PM: grabs for salty or sweet snack like popcorn or chocolate covered almonds, coffee or tea</p></li><li><p>Dinner: Fresh fish, sweet potatoes, and green vegetable, water</p></li></ul>`,
      },
    },
    {
      localId: 'PaulJackson',
      firstName: 'Paul',
      lastName: 'Jackson',
      createdAt: ZonedDateTime.parse('2021-06-01T00:00:00.0Z'),
      coachId,
      pronoun: 'He / Him',
      nutritionistId,
      lastAppActivity: ZonedDateTime.now(ZoneId.UTC).minusDays(1),
      shouldStarUser: true,
      videos: ['a05dfa26-ee94-483f-aee3-658f74457c15'],
      shouldCreateSessions: true,
      shouldSendMessage: true,
      dailyActionConfig: {
        type: CoachTouchPointType.TrackAttendance,
        due: now,
      },
      onboardingSurveys: {
        birthday: LocalDate.now().minusYears(45).toString(),
        height: 5.83,
        weight: 300,
        identity: 0,
        goals: 1,
        motivation: 3,
        barriers: [0, 2, 6],
        passions: [2, 3],
        experience: 1,
        movement1: 1,
        movement2: 2,
        nutrition1: 1,
        nutrition2: 2,
        recovery1: 1,
        recovery2: 2,
        recovery3: 3,
        mindset1: 1,
        mindset2: 3,
        mindset3: 1,
      },
      note: {
        coachId,
        title: 'Coaching Session Notes',
        content:
          '<p>Works in human resources. Works onsite 5x/week.&nbsp;</p><p>Played football growing up</p><p>Gained 100 lbs in last 15 years since getting married &amp; working stressful desk job</p><p>Diagnosed with type II diabetes five years ago.</p><p></p><p><strong>Family</strong></p><ul><li><p>Wife x 15 years</p></li><li><p>2 sons, 14 &amp; 10 years old ~ AJ and Shawn</p></li></ul><p><strong>Goals&nbsp;</strong></p><ul><li><p>Wants to be old self 100+ lbs ago. Feels horrible &amp; sluggish. Low confidence. Where should I start?&nbsp;</p></li><li><p>Has tried onsite fitness center. Does elliptical and has tried group ex class.&nbsp;</p></li><li><p>Use to go on walks with wife &amp; kids for 20 minutes after dinner most nights. Got out of the habit once he started going back to office after covid.&nbsp;</p></li></ul><p><strong>Diet</strong></p><ul><li><p>Eats Subway most days for lunch</p></li><li><p>Enjoys cooking dinners at home with wife and songs</p></li><li><p>Spaghetti, taco tuesdays, burgers on the grill, bbq chicken</p></li></ul>',
      },
    },
    {
      localId: 'AnithaReddy',
      firstName: 'Anitha',
      lastName: 'Reddy',
      pronoun: 'She / Her',
      createdAt: ZonedDateTime.parse('2023-06-01T00:00:00.0Z'),
      coachId,
      nutritionistId,
      lastAppActivity: ZonedDateTime.now(ZoneId.UTC).minusDays(2),
      shouldStarUser: true,
      shouldCreateSessions: true,
      shouldSendMessage: true,
      videos: [
        'a50837b8-bbae-4d01-9304-9d0663d9129e',
        'e8655f11-874b-4d80-b68c-02c9a4edaf3d',
        'b44cc6f0-06b5-4c1d-a0d4-9d55a3581ecf',
        '3f6cd331-b966-44c7-9a14-3c7864607a6b',
        '88dad17b-2ae4-4fde-bcb2-97513bd8291d',
        '3912f72a-62a1-409a-ae2d-196ee9316d9c',
        '49886512-8317-44dd-9be5-81912068a0e3',
        '4f2a6caf-4319-4343-936c-a9e3abd37999',
        'cbb4084d-5979-4f8d-b9f7-59ef6c33f1f4',
        'f6f298b7-8f6a-401e-a127-a13b75cd722d',
      ],
      dailyActionConfig: {
        type: CoachTouchPointType.SessionReminder,
        due: now,
      },
      onboardingSurveys: {
        birthday: LocalDate.now().minusYears(26).toString(),
        height: 5.17,
        weight: 100,
        identity: 1,
        goals: 1,
        motivation: 1,
        barriers: [5],
        passions: [7, 11],
        experience: 0,
        movement1: 0,
        movement2: 4,
        nutrition1: 4,
        nutrition2: 2,
        recovery1: 2,
        recovery2: 2,
        recovery3: 3,
        mindset1: 3,
        mindset2: 3,
        mindset3: 4,
      },
      note: {
        coachId,
        title: 'Coaching Session Note',
        content: `<p>Works in creative design and marketing. Works from home</p><p>Not active and has only done cardio for workouts in the past.&nbsp; 5 years ago did a 5k with a friend.&nbsp;</p><p></p><p><strong>Goals:&nbsp;</strong></p><ul><li><p>Tone up and lose weight &amp; improve cholesterol levels.&nbsp;</p></li><li><p>Wants to learn how to be healthier and start a diet &amp; fitness routine</p></li><li><p>Wants to try strength training but is intimidated by the gym</p></li></ul><p>Available 2-3x per week for 30 minutes for workouts</p><p>Eats out for most meals. Bagel or oatmeal for breakfast.&nbsp;</p><p>Fruit and granola bar for snack at work</p>`,
      },
    },
  ];
};

export const getHpCoachTrainingMembers = ({
  coachId,
}: {
  coachId: string;
}): AdminDemoUserInput[] => {
  const now = ZonedDateTime.now(ZoneId.UTC);
  const zaphodsPlanStartDate = LocalDate.now().with(
    TemporalAdjusters.previousOrSame(DayOfWeek.SUNDAY),
  );

  return [
    {
      localId: 'ArthurDent',
      firstName: 'Arthur',
      lastName: 'Dent',
      createdAt: ZonedDateTime.parse('2023-06-01T00:00:00.0Z'),
      coachId,
      hasHighPerformanceCoaching: true,
      shouldCreateHistoricalData: true,
      shouldStarUser: true,
      userGoal: {
        type: UserGoalType.EnergyLevels,
        description: 'Have more energy',
      },
      onboardingSurveys: {
        birthday: LocalDate.now().minusYears(38).toString(),
        height: 5.92,
        weight: 180,
        identity: 1,
        goals: 0,
        motivation: 2,
        barriers: [0, 2, 4],
        passions: [7, 8, 9],
      },
    },
    {
      localId: 'ZaphodBeeblebrox',
      firstName: 'Zaphod',
      lastName: 'Beeblebrox',
      createdAt: ZonedDateTime.parse('2023-06-01T00:00:00.0Z'),
      coachId,
      hasHighPerformanceCoaching: true,
      shouldCreateHistoricalData: true,
      shouldStarUser: true,
      userGoal: {
        type: UserGoalType.PhysicalPerformance,
        description: 'Ultramarathon',
      },
      onboardingSurveys: {
        birthday: LocalDate.now().minusYears(38).toString(),
        height: 5.92,
        weight: 180,
        identity: 0,
        goals: 0,
        motivation: 2,
        barriers: [0, 3, 6],
        passions: [11, 10, 14],
      },
      assessments: {
        assigned: {
          surveyContentIds: [
            '7a4b706d-0a50-4365-8d88-3aa0594cc645',
            '08147068-1a73-451c-86b1-cf9294ac0019',
            '7c5aec40-7f6e-4128-bef5-613b57055265',
            '7482534f-3162-478a-8c7b-2c3da5da23eb',
            '897810ff-8139-47b9-a0c9-4d3602dd4ced',
            '82b84a12-3c79-4366-a0d6-6bbadab27927',
          ],
        },
        completed: [
          {
            surveyContentId: '7a4b706d-0a50-4365-8d88-3aa0594cc645',
            answers: [
              {
                selectedChoices: [1],
              },
              {
                selectedChoices: [2],
              },
              {
                selectedChoices: [3],
              },
              {
                selectedChoices: [3],
              },
            ],
          },
          {
            surveyContentId: '08147068-1a73-451c-86b1-cf9294ac0019',
            answers: [
              {
                selectedChoices: [2],
              },
              {
                selectedChoices: [1],
              },
              {
                selectedChoices: [3],
              },
              {
                selectedChoices: [3],
              },
            ],
          },
          {
            surveyContentId: '7c5aec40-7f6e-4128-bef5-613b57055265',
            answers: [
              {
                selectedChoices: [2],
              },
              {
                selectedChoices: [2],
              },
              {
                selectedChoices: [4],
              },
              {
                selectedChoices: [2],
              },
            ],
          },
          {
            surveyContentId: '7482534f-3162-478a-8c7b-2c3da5da23eb',
            answers: [
              {
                selectedChoices: [0],
              },
              {
                selectedChoices: [3],
              },
            ],
          },
          {
            surveyContentId: '897810ff-8139-47b9-a0c9-4d3602dd4ced',
            answers: [
              {
                selectedChoices: [3],
              },
              {
                selectedChoices: [4],
              },
              {
                selectedChoices: [0],
              },
            ],
          },
          {
            surveyContentId: '82b84a12-3c79-4366-a0d6-6bbadab27927',
            answers: [
              {
                selectedChoices: [0],
              },
              {
                selectedChoices: [4],
              },
              {
                selectedChoices: [4],
              },
              {
                selectedChoices: [3],
              },
              {
                selectedChoices: [3],
              },
            ],
          },
        ],
      },
      customPlans: [
        {
          isTest: true,
          name: 'Phase 1',
          startDate: zaphodsPlanStartDate.toString(),
          planItems: [
            // Week 1 Day 1
            {
              date: zaphodsPlanStartDate.toString(),
              type: PlanItemType.CustomTask,
              order: 0,
              title: 'Weekly Overview',
              component: 'Reflection',
              description:
                "Hey Zaphod!  Welcome to the first week of your Gameplan.  To help focus on developing your strength and power we'll have four days of training this week, supported with lots of great info and strategies on fueling, and sleep among other things.  Each of these have been selected based on the information you've provided me to help you take a big step towards being ready for your moment!  So choose to meet these with curiosity, and an open mind, and I'll reach out later this week to see how everything is going!",
            },
            // Week 1 Day 2
            {
              date: zaphodsPlanStartDate.plusDays(1).toString(),
              type: PlanItemType.ExosContent,
              order: 0,
              component: 'Training',
              contentId: '896bf098-b59c-439f-adc5-14ce998a0992',
            },
            {
              date: zaphodsPlanStartDate.plusDays(1).toString(),
              type: PlanItemType.ExosContent,
              order: 1,
              component: 'Fueling',
              contentId: '0c439519-636d-4f9c-92ae-f88676097e7d',
            },
            {
              date: zaphodsPlanStartDate.plusDays(1).toString(),
              type: PlanItemType.ExosContent,
              order: 2,
              component: 'Daily Movement',
              contentId: '9f4a91e5-f283-4d9c-8d57-e0d7a290d094',
            },
            // Week 1 Day 3
            {
              date: zaphodsPlanStartDate.plusDays(2).toString(),
              type: PlanItemType.ExosContent,
              order: 0,
              component: 'Training',
              contentId: '513c5e07-d5e7-4477-81c8-d52a4d1cddcb',
            },
            {
              date: zaphodsPlanStartDate.plusDays(2).toString(),
              type: PlanItemType.ExosContent,
              order: 1,
              component: 'Fueling',
              contentId: '4d5d400b-3d3d-444d-a2da-ee03a99a203b',
            },
            {
              date: zaphodsPlanStartDate.plusDays(2).toString(),
              type: PlanItemType.ExosContent,
              order: 2,
              component: 'Daily Movement',
              contentId: 'bb2e2c78-e075-45bb-94e7-83451adeae18',
            },

            // Week 1 Day 4
            {
              date: zaphodsPlanStartDate.plusDays(3).toString(),
              type: PlanItemType.ExosContent,
              order: 0,
              component: 'Sleep',
              contentId: 'e58168ff-8db0-4e60-9a94-2d77777d650d',
            },
            {
              date: zaphodsPlanStartDate.plusDays(3).toString(),
              type: PlanItemType.ExosContent,
              order: 1,
              component: 'Reflection',
              contentId: 'c3a0e928-0a08-410f-bffc-32e58d5cbeb5',
            },
            {
              date: zaphodsPlanStartDate.plusDays(3).toString(),
              type: PlanItemType.ExosContent,
              order: 2,
              component: 'Regulation',
              contentId: '897d2e88-1073-478b-a05b-a789f24e1637',
            },

            // Week 1 Day 5
            {
              date: zaphodsPlanStartDate.plusDays(4).toString(),
              type: PlanItemType.ExosContent,
              order: 0,
              component: 'Training',
              contentId: 'e87d7384-cc4c-4616-bcb6-fad4eb7f3cc4',
            },
            {
              date: zaphodsPlanStartDate.plusDays(4).toString(),
              type: PlanItemType.ExosContent,
              order: 1,
              component: 'Fueling',
              contentId: '6965b7f8-f697-46c2-8ca6-3b6758faf6e5',
            },
            {
              date: zaphodsPlanStartDate.plusDays(4).toString(),
              type: PlanItemType.CustomTask,
              order: 2,
              component: 'Sleep',
              title: 'Natural light',
              description:
                'Remember that natural light is an incredibly strong anchor for your circadian rhythm.  So make sure to get 2-10min before 10a, and another 2-10min around sunset as well.',
            },
            {
              date: zaphodsPlanStartDate.plusDays(4).toString(),
              type: PlanItemType.ExosContent,
              order: 3,
              component: 'Daily Movement',
              contentId: '2137d013-c509-4102-82d7-25a5be83deb5',
            },

            // Week 1 Day 6
            {
              date: zaphodsPlanStartDate.plusDays(5).toString(),
              type: PlanItemType.ExosContent,
              order: 0,
              component: 'Training',
              contentId: '3c0d3539-976a-4741-8f5b-5711d7940907',
            },
            {
              date: zaphodsPlanStartDate.plusDays(5).toString(),
              type: PlanItemType.ExosContent,
              order: 1,
              component: 'Fueling',
              contentId: 'fe02e3a6-97eb-420f-a678-7a69867e2a37',
            },
            {
              date: zaphodsPlanStartDate.plusDays(5).toString(),
              type: PlanItemType.CustomTask,
              order: 2,
              component: 'Sleep',
              title: 'Natural light',
              description:
                'Remember that natural light is an incredibly strong anchor for your circadian rhythm.  So make sure to get 2-10min before 10a, and another 2-10min around sunset as well.',
            },
            {
              date: zaphodsPlanStartDate.plusDays(5).toString(),
              type: PlanItemType.ExosContent,
              order: 3,
              component: 'Daily Movement',
              contentId: '7e9edc96-4e11-4b4d-98d4-1de66e7c9d9b',
            },

            // Week 1 Day 7
            {
              date: zaphodsPlanStartDate.plusDays(6).toString(),
              type: PlanItemType.CustomTask,
              order: 0,
              component: 'Sleep',
              title: 'Natural light',
              description:
                'Remember that natural light is an incredibly strong anchor for your circadian rhythm.  So make sure to get 2-10min before 10a, and another 2-10min around sunset as well.',
            },
            {
              date: zaphodsPlanStartDate.plusDays(6).toString(),
              type: PlanItemType.CustomTask,
              order: 1,
              component: 'Regulation',
              title: 'Breath Work to Stay Calm',
              description:
                'Keep in mind that breath work which extends the exhale compared to the inhale can be an excellent tool to help you keep your cool in heated moments. So as you get ready for next week, take a couple of minutes to reset your nervous system with a few exhale emphasis breath.  3 rounds of a 4-7-8 breath with a 4sec inhale, 7sec hold, and 8sec exhale is a great way to get started!',
            },
          ],
        },
      ],
    },
    {
      localId: 'PeterJefferson',
      firstName: 'Peter',
      lastName: 'Jefferson',
      hasHighPerformanceCoaching: true,
      createdAt: ZonedDateTime.parse('2021-06-01T00:00:00.0Z'),
      coachId,
      pronoun: 'He / Him',
      lastAppActivity: ZonedDateTime.now(ZoneId.UTC).minusDays(1),
      shouldStarUser: true,
      videos: ['a05dfa26-ee94-483f-aee3-658f74457c15'],
      shouldCreateSessions: true,
      shouldSendMessage: true,
      dailyActionConfig: {
        type: CoachTouchPointType.TrackAttendance,
        due: now,
      },
      userGoal: {
        type: UserGoalType.EnergyLevels,
        description: `- Increase daily energy and focus through better sleep, stress management, and consistent movement.
- Develop realistic and sustainable fitness habits that fit his work/life schedule.
- Improve nutrition—especially at work—and support better blood sugar management.
- Rebuild confidence and resilience as a leader and father.
- Re-establish healthy habits with his family (e.g., evening walks, active weekends).`,
      },
      assessments: {
        assigned: {
          surveyContentIds: [
            '873a388d-725a-4ba6-b7cf-ee7e43cdbcc7', // "Self-Regulation"
            'ebcbbeec-245a-45eb-86c5-35cf82f395f2', // "Flowability"
            '6e121999-c0fa-47ae-95d6-9a68523e644c', // "Fuel Utilization"
            '7a4b706d-0a50-4365-8d88-3aa0594cc645', // "Nutrient Profile"
            '1ac78947-1b98-4b3b-abdd-ad1fcce2b9ac', // "Grit"
            '82b84a12-3c79-4366-a0d6-6bbadab27927', // "Sleep Proficiency"
          ],
        },
        completed: [
          {
            surveyContentId: '873a388d-725a-4ba6-b7cf-ee7e43cdbcc7', // "Self-Regulation"
            answers: [
              {
                selectedChoices: [3],
              },
              {
                selectedChoices: [1],
              },
              {
                selectedChoices: [1],
              },
              {
                selectedChoices: [3],
              },
            ],
          },
          {
            surveyContentId: 'ebcbbeec-245a-45eb-86c5-35cf82f395f2', // "Flowability"
            answers: [
              {
                selectedChoices: [2],
              },
              {
                selectedChoices: [2],
              },
              {
                selectedChoices: [1],
              },
              {
                selectedChoices: [3],
              },
            ],
          },
          {
            surveyContentId: '6e121999-c0fa-47ae-95d6-9a68523e644c', // "Fuel Utilization"
            answers: [
              {
                selectedChoices: [2],
              },
              {
                selectedChoices: [2],
              },
              {
                selectedChoices: [1],
              },
              {
                selectedChoices: [1],
              },
            ],
          },
          {
            surveyContentId: '7a4b706d-0a50-4365-8d88-3aa0594cc645', // "Nutrient Profile"
            answers: [
              {
                selectedChoices: [3],
              },
              {
                selectedChoices: [1],
              },
              {
                selectedChoices: [2],
              },
              {
                selectedChoices: [2],
              },
            ],
          },
          {
            surveyContentId: '1ac78947-1b98-4b3b-abdd-ad1fcce2b9ac', // "Grit"
            answers: [
              {
                selectedChoices: [0],
              },
              {
                selectedChoices: [1],
              },
              {
                selectedChoices: [2],
              },
              {
                selectedChoices: [1],
              },
            ],
          },
          {
            surveyContentId: '82b84a12-3c79-4366-a0d6-6bbadab27927', // "Sleep Proficiency"
            answers: [
              {
                selectedChoices: [2],
              },
              {
                selectedChoices: [4],
              },
              {
                selectedChoices: [3],
              },
              {
                selectedChoices: [3],
              },
              {
                selectedChoices: [4],
              },
            ],
          },
        ],
      },
      onboardingSurveys: {
        birthday: LocalDate.now().minusYears(45).toString(),
        height: 5.83,
        weight: 300,
        identity: 0,
        goals: 1,
        motivation: 3,
        barriers: [0, 2, 6],
        passions: [2, 3],
        experience: 1,
        movement1: 1,
        movement2: 2,
        nutrition1: 1,
        nutrition2: 2,
        recovery1: 1,
        recovery2: 2,
        recovery3: 3,
        mindset1: 1,
        mindset2: 3,
        mindset3: 1,
      },
      note: {
        coachId,
        title: 'Coaching Session Notes',
        content:
          '<h3><strong>Current Life &amp; Career Situation</strong></h3><p>Peter has been with the same company for nearly two decades, rising steadily from HR assistant to Director. Known as a solid, dependable leader, he’s well-liked by staff and respected by upper management for his grounded, no-nonsense approach.</p><p>But that consistent climb has come at a cost—mainly his health, energy, and personal well-being.</p><ul><li><p><strong>Workload:</strong> Manages a team of 15 HR professionals across recruiting, benefits, and compliance. Constantly pulled into fire-fighting and policy enforcement. Long hours. </p></li><li><p><strong>Office Time:</strong> Fully onsite post-COVID, works 7:30 a.m. to 6:00 p.m. most days, sometimes logs in again after dinner. </p></li><li><p><strong>Leadership Style:</strong> Steady, team-focused, but lately low-energy and reactive. He worries he’s not setting a great example. </p></li></ul><h3><strong>Health &amp; Lifestyle</strong></h3><ul><li><p><strong>Weight:</strong> Gained ~50 lbs over 15 years. Recently hit a new high on the scale. </p></li><li><p><strong>Medical:</strong> Diagnosed with Type 2 diabetes 5 years ago. Hasn’t been consistent with lifestyle changes or medication adherence. Suspects sleep apnea but hasn’t followed up with a specialist. </p></li><li><p><strong>Exercise:</strong> Uses onsite gym 1–2x/week—mostly elliptical. Tried a group fitness class but felt out of place. Hasn’t found a sustainable rhythm. </p></li><li><p><strong>Diet: </strong></p><ul><li><p>Skips or rushes through lunch (often catered sandwiches or pizza at meetings). </p></li><li><p>Loves cooking with family—makes hearty meals like spaghetti, taco Tuesdays, burgers, and BBQ chicken. </p></li><li><p>Eats late, sometimes snacks after dinner while working. </p></li></ul></li><li><p><strong>Sleep:</strong> Struggles to fall asleep. Wakes up feeling groggy and unrested. Afternoon crashes are common and interfere with productivity. </p></li></ul><h3><strong>Mindset &amp; Pain Points</strong></h3><ul><li><p><strong>Wants:</strong> To feel strong and energized again. To be a role model for his sons. To improve his health without losing his career momentum. </p></li><li><p><strong>Fears:</strong> That his health will decline to the point where it impacts his family—or forces him to step back from work. He worries about being perceived as “lazy” or undisciplined. </p></li><li><p><strong>Obstacles:</strong> Thinks he has no time. Feels too tired to work out after work. Doesn’t like “trendy” wellness advice. Struggles with consistency and follow-through. </p></li></ul><h3><strong>Goals (With Coaching)</strong></h3><ul><li><p>Increase daily energy and focus through better sleep, stress management, and consistent movement.</p></li><li><p>Develop realistic and sustainable fitness habits that fit his work/life schedule.</p></li><li><p>Improve nutrition—especially at work—and support better blood sugar management.</p></li><li><p>Rebuild confidence and resilience as a leader and father.</p></li><li><p>Re-establish healthy habits with his family (e.g., evening walks, active weekends).</p></li></ul>',
      },
    },
    {
      localId: 'AnnieRebaker',
      firstName: 'Annie',
      lastName: 'Rebaker',
      pronoun: 'She / Her',
      hasHighPerformanceCoaching: true,
      createdAt: ZonedDateTime.parse('2023-06-01T00:00:00.0Z'),
      coachId,
      lastAppActivity: ZonedDateTime.now(ZoneId.UTC).minusDays(2),
      shouldStarUser: true,
      shouldCreateSessions: true,
      shouldSendMessage: true,
      videos: [
        'a50837b8-bbae-4d01-9304-9d0663d9129e',
        'e8655f11-874b-4d80-b68c-02c9a4edaf3d',
        'b44cc6f0-06b5-4c1d-a0d4-9d55a3581ecf',
        '3f6cd331-b966-44c7-9a14-3c7864607a6b',
        '88dad17b-2ae4-4fde-bcb2-97513bd8291d',
        '3912f72a-62a1-409a-ae2d-196ee9316d9c',
        '49886512-8317-44dd-9be5-81912068a0e3',
        '4f2a6caf-4319-4343-936c-a9e3abd37999',
        'cbb4084d-5979-4f8d-b9f7-59ef6c33f1f4',
        'f6f298b7-8f6a-401e-a127-a13b75cd722d',
      ],
      dailyActionConfig: {
        type: CoachTouchPointType.SessionReminder,
        due: now,
      },
      userGoal: {
        type: UserGoalType.EmotionalWellbeing,
        description: `- Restore executive presence and confidence.
- Rebuild personal resilience and energy (sleep, health, boundaries).
- Lead her team with clarity, vision, and empathy.
- Hit deadlines consistently and with excellence.
- Reclaim her identity outside of work—be a healthier, more present mom.`,
      },
      assessments: {
        assigned: {
          surveyContentIds: [
            'b0d0b02d-2fd7-4f0a-9e89-b0c99483b1a3', // "Inner Awareness"
            '873a388d-725a-4ba6-b7cf-ee7e43cdbcc7', // "Self-Regulation"
            '6e121999-c0fa-47ae-95d6-9a68523e644c', // "Fuel Utilization"
            '7482534f-3162-478a-8c7b-2c3da5da23eb', // "Endurance"
            '82b84a12-3c79-4366-a0d6-6bbadab27927', // "Sleep Proficiency"
            '1ac78947-1b98-4b3b-abdd-ad1fcce2b9ac', // "Grit"
          ],
        },
        completed: [
          {
            surveyContentId: 'b0d0b02d-2fd7-4f0a-9e89-b0c99483b1a3', // "Inner Awareness"
            answers: [
              {
                selectedChoices: [3],
              },
              {
                selectedChoices: [4],
              },
              {
                selectedChoices: [2],
              },
              {
                selectedChoices: [3],
              },
            ],
          },
          {
            surveyContentId: '873a388d-725a-4ba6-b7cf-ee7e43cdbcc7', // "Self-Regulation"
            answers: [
              {
                selectedChoices: [2],
              },
              {
                selectedChoices: [2],
              },
              {
                selectedChoices: [1],
              },
              {
                selectedChoices: [3],
              },
            ],
          },
          {
            surveyContentId: '6e121999-c0fa-47ae-95d6-9a68523e644c', // "Fuel Utilization"
            answers: [
              {
                selectedChoices: [4],
              },
              {
                selectedChoices: [1],
              },
              {
                selectedChoices: [1],
              },
              {
                selectedChoices: [1],
              },
            ],
          },
          {
            surveyContentId: '7482534f-3162-478a-8c7b-2c3da5da23eb', // "Endurance"
            answers: [
              {
                selectedChoices: [0],
              },
              {
                selectedChoices: [3],
              },
            ],
          },
          {
            surveyContentId: '1ac78947-1b98-4b3b-abdd-ad1fcce2b9ac', // "Grit"
            answers: [
              {
                selectedChoices: [0],
              },
              {
                selectedChoices: [1],
              },
              {
                selectedChoices: [2],
              },
              {
                selectedChoices: [1],
              },
            ],
          },
          {
            surveyContentId: '82b84a12-3c79-4366-a0d6-6bbadab27927', // "Sleep Proficiency"
            answers: [
              {
                selectedChoices: [2],
              },
              {
                selectedChoices: [1],
              },
              {
                selectedChoices: [1],
              },
              {
                selectedChoices: [2],
              },
              {
                selectedChoices: [1],
              },
            ],
          },
        ],
      },
      onboardingSurveys: {
        birthday: LocalDate.now().minusYears(26).toString(),
        height: 5.17,
        weight: 100,
        identity: 1,
        goals: 1,
        motivation: 1,
        barriers: [5],
        passions: [7, 11],
        experience: 0,
        movement1: 0,
        movement2: 4,
        nutrition1: 4,
        nutrition2: 2,
        recovery1: 2,
        recovery2: 2,
        recovery3: 3,
        mindset1: 3,
        mindset2: 3,
        mindset3: 4,
      },
      note: {
        coachId,
        title: 'Coaching Session Note',
        content: `<h3><strong>Current Life &amp; Career Situation</strong></h3><p>Annie has spent the last 18 years climbing the creative corporate ladder. She’s brilliant, imaginative, and known for trendsetting work that has shaped the visual identity of her company. But in the last 18 months, she's felt like she’s slipping—both personally and professionally.</p><ul><li><p><strong>Workload:</strong> Constantly pulled into high-stakes meetings and client presentations. Her creative department is understaffed, and she often ends up doing the work herself.</p></li><li><p><strong>Leadership:</strong> She’s aware that her emotional tone affects her team. She's short-tempered, distracted, and no longer mentoring her junior staff.</p></li><li><p><strong>Deadlines:</strong> She has missed three major campaign milestones this quarter, a red flag for senior leadership.</p></li><li><p><strong>Reputation Risk:</strong> She’s terrified that she’s becoming known as "difficult" or "unreliable" in an industry where image is everything.</p></li></ul><h3><strong>Health &amp; Lifestyle</strong></h3><ul><li><p><strong>Sleep:</strong> Averages 5-6 hours per night, often waking up at 3 a.m. to check emails or tweak design drafts.</p></li><li><p><strong>Diet:</strong> Eats irregularly—skips breakfast, grabs takeout most nights, often eats while working.</p></li><li><p><strong>Exercise:</strong> Used to attend boutique fitness classes 3x a week but hasn’t gone in 6 months.</p></li><li><p><strong>Stress Management:</strong> High-functioning anxiety. Relies on caffeine and occasional wine to get through the day/week.</p></li><li><p><strong>Medical:</strong> Recently diagnosed with adrenal fatigue and mild hypertension.</p></li></ul><h3><strong>Mindset &amp; Pain Points</strong></h3><ul><li><p><strong>Wants:</strong> To feel in control again. To show up as a strong, composed leader. To reconnect with her creativity.</p></li><li><p><strong>Fears:</strong> That she’s failing as both a leader and a parent. That she’s burnt out and replaceable.</p></li><li><p><strong>Obstacles:</strong> Feels too busy to invest time in herself. Guilty when not working. Doesn’t know how to say “no.”</p></li><li><p><strong>Awareness:</strong> She’s started to realize that talent alone won’t carry her anymore—she needs systems, mindset work, and sustainable routines. </p></li></ul><h3><strong>Goals (with coaching)</strong></h3><ul><li><p>Restore executive presence and confidence.</p></li><li><p>Rebuild personal resilience and energy (sleep, health, boundaries).</p></li><li><p>Lead her team with clarity, vision, and empathy.</p></li><li><p>Hit deadlines consistently and with excellence.</p></li><li><p>Reclaim her identity outside of work—be a healthier, more present mom.</p></li></ul>`,
      },
    },
    {
      localId: 'MartikaPhillip',
      firstName: 'Martika',
      lastName: 'Phillip',
      createdAt: ZonedDateTime.parse('2022-07-01T00:00:00.0Z'),
      coachId,
      hasHighPerformanceCoaching: true,
      pronoun: 'She / Her',
      lastAppActivity: ZonedDateTime.now(ZoneId.UTC),
      shouldStarUser: true,
      shouldCreateHistoricalData: true,
      shouldAssignPractice: true,
      shouldCreateSessions: true,
      shouldSendMessage: true,
      videos: [
        '6f453678-2742-4975-a830-d73156d4f5d2',
        'a5c667e5-2d4a-4ff1-bcef-6902c8a1cc17',
      ],
      dailyActionConfig: {
        type: CoachTouchPointType.OneWeekPostSessionCheckIn,
        due: now,
      },
      onboardingSurveys: {
        birthday: LocalDate.now().minusYears(38).toString(),
        height: 5.5,
        weight: 128,
        identity: 1,
        goals: 0,
        motivation: 3,
        barriers: [0, 2, 6],
        passions: [18, 12, 15],
        experience: 2,
        movement1: 3,
        movement2: 4,
        nutrition1: 2,
        nutrition2: 4,
        recovery1: 2,
        recovery2: 1,
        recovery3: 3,
        mindset1: 3,
        mindset2: 3,
        mindset3: 2,
      },
      note: {
        coachId,
        title: 'Coaching Session Note',
        content: `<h3>Goal</h3><ul><li><p>Avid runner since college. Competes in a marathon each year</p></li><li><p>Next marathon is 4 months away. Looking for a new PR <strong>(under 4 hrs)</strong></p></li><li><p>Typically is a Go-Getter, occasionally finds she struggles with team members who do not have same ambition level as her.</p></li></ul><p><strong>Typical run schedule:&nbsp;</strong></p><ul><li><p>4x/week</p></li><li><p>1 track day, 2 medium distance days, 1 long day</p></li></ul><p>*Felt fatigued lately. Having trouble going to sleep and sometimes wakes up in middle of the night.&nbsp;</p><p>*Right knee pain during her running lately</p><p>*Has never hired a trainer or endurance coach. Wondering if we can help her set a PR</p><p><strong>&nbsp;Typical day of eating:&nbsp;</strong></p><ul><li><p>Breakfast: 2 eggs, ½ cup oatmeal, fresh berries, coffee, water</p></li><li><p>Snack AM: greek yogurt, granola, and/or protein shake smoothie</p></li><li><p>Lunch: Spinach salad with grilled chicken, vegetable variety, avocado oil,water</p></li><li><p>Snack PM: grabs for salty or sweet snack like popcorn or chocolate covered almonds, coffee or tea</p></li><li><p>Dinner: Fresh fish, sweet potatoes, and green vegetable, water</p></li></ul>`,
      },
    },
  ];
};

export const getTaskCoachTrainingMembers = ({
  coachId,
  nutritionistId,
}: {
  coachId: string;
  nutritionistId?: string;
}): AdminDemoUserInput[] => {
  const now = ZonedDateTime.now(ZoneId.UTC);

  return [
    {
      localId: 'NewMemberActivationAtRisk-now',
      coachId,
      dailyActionConfig: {
        type: CoachTouchPointType.NewMemberActivationAtRisk,
        due: now,
      },
    },
    {
      localId: 'NewMemberActivationAtRisk-yesterday',
      coachId,
      dailyActionConfig: {
        type: CoachTouchPointType.NewMemberActivationAtRisk,
        due: now.minusDays(1),
      },
    },
    {
      localId: 'OneWeekPostSessionCheckIn-now',
      coachId,
      dailyActionConfig: {
        type: CoachTouchPointType.OneWeekPostSessionCheckIn,
        due: now,
      },
    },
    {
      localId: 'SessionReminder-now',
      coachId,
      dailyActionConfig: {
        type: CoachTouchPointType.SessionReminder,
        due: now,
      },
    },
    {
      localId: 'SessionReminder-now-rescheduled',
      coachId,
      dailyActionConfig: {
        type: CoachTouchPointType.SessionReminder,
        due: now,
        isRescheduled: true,
      },
    },
    {
      localId: 'SessionReminder-yesterday-cancelled',
      coachId,
      dailyActionConfig: {
        type: CoachTouchPointType.SessionReminder,
        due: now.minusDays(1),
        isCancelled: true,
      },
    },
    {
      localId: 'TrackAttendance-nutrition',
      coachId,
      nutritionistId,
      shouldCreateSessions: true,
      shouldCreatePersonalTrainingSession: true,
      dailyActionConfig: {
        type: CoachTouchPointType.TrackAttendance,
        due: now.minusDays(4),
      },
    },
    {
      localId: 'TrackAttendance-pt',
      coachId,
      shouldCreateSessions: true,
      shouldCreatePersonalTrainingSession: true,
      dailyActionConfig: {
        type: CoachTouchPointType.TrackAttendance,
        sessionType: CoachSessionType.PersonalTraining,
        due: now,
      },
    },
    {
      localId: 'Custom-twodaysfromnow',
      coachId,
      dailyActionConfig: {
        type: CoachTouchPointType.Custom,
        due: now.plusDays(2),
      },
    },
  ];
};

export const getPerformanceCoachingTrainingMembers = ({
  coachId,
}: {
  coachId: string;
}): AdminDemoUserInput[] => {
  const now = ZonedDateTime.now(ZoneId.UTC);
  const officeLocationId = 'c57a2e8f-9b6f-4e50-8b7f-c4ad1b4a3e89'; // "My office is not listed"

  const members: AdminDemoUserInput[] = [
    {
      // Claudia Smith - Employee, Active: Yesterday, Joined: January 2026
      // Coach Experience: ON, Upcoming session: Yes, Pre-assessment: Assigned and Completed
      coachId,
      localId: 'ClaudiaSmith',
      firstName: 'Claudia',
      lastName: 'Smith',
      email: `coachled-member-${Date.now()}-1@testuser.ex-os.net`,
      createdAt: now.minusDays(1),
      lastAppActivity: now.minusDays(1),
      officeLocationId,
      shouldCreateSessions: true,
      memberExperience: MemberExperience.CoachLed,
      completeGameplanPreassessments: true,
      lowestScoringPQ: 'Force Production',
      note: {
        coachId,
        title: 'Initial 7.16.26',
        content: `<p>7.16.26</p><p>Focus Area: Muscle Growth&nbsp;</p><p>PQ: Force Prod 23%, Body Comp 59%, Load Accumulation 63%, Sleep Proficiency 63%</p><p data-placeholder="Write here to leave a note" class="is-empty"><br class="ProseMirror-trailingBreak"></p><p><strong>Goal &amp; Why:</strong> Mom of 2 (12 &amp; 14). Dedicated 10+ years to kids; has not taken care of self. Lacks energy &amp; confidence to make change. Wants to lose 20 lbs &amp; gain energy to play with kids after a long work day.</p><p><br class="ProseMirror-trailingBreak"></p><p><strong>Training History &amp; Availability:</strong> Minimal strength training; likes dancing with daughters open to doing this more often in evenings before or during dinner prep. Used to run 5+ years ago, afraid to jump back into it. Current: 2x/wk walking dog (12-15 min).&nbsp;</p><p>Availability: Fitness Center: Tues, Wed, Thurs around lunch or after work (except Wed). Home Gym w/ DB</p><p>Weekends available for longer 30 min workouts</p><p>Pain: Chronic neck &amp; shoulder pain (5+ years).</p><p><br class="ProseMirror-trailingBreak"></p><p><strong>Goals &amp; 6 wk Outcome</strong> Wants to have more energy at end of day &amp; learn to lift weights confidently enough to do it independently. Open to learning proper form.</p><p><br class="ProseMirror-trailingBreak"></p><p><strong>Context &amp; Constraints</strong> Commitment: 3-4x/wk for 30 min. 40 min commute. FCM 3x/wk. WFH Mon &amp; Fridays. Barriers: Commute, energy, cold weather/ winter excuses. Needs accountability.</p><p><br class="ProseMirror-trailingBreak"></p><p><strong>Sleep</strong> Late (11:30-12) on phone/TV. Aware this impacts energy.</p><p><br class="ProseMirror-trailingBreak"></p><p><strong>Nutrition</strong> Knows what to eat but lacks energy to prep. Picks up food most nights. Future focus area but not interested in focusing on it at this current time.</p>`,
      },
      onboardingSurveys: {
        birthday: LocalDate.now().minusYears(42).toString(),
        height: 5.83, // 5'10"
        weight: 170,
        identity: 1, // Female
        focusArea: 4, // Muscle Growth
        motivation: 1, // Ready but need help getting started
        barriers: [0, 2, 6], // Time, Family, No energy
        passions: [2, 3], // Cooking, Dancing
        experience: 1, // I go through phases
        movement1: 1, // 1-2 days active
        movement2: 2, // Shins
        nutrition1: 1, // Rarely
        nutrition2: 2, // 41-60 oz
        recovery1: 1, // 5-6 hrs
        recovery2: 2, // Sometimes
        recovery3: 3, // Often
        mindset1: 1, // Rarely
        mindset2: 3, // Often
        mindset3: 1, // Rarely
      },
    },
    {
      // Jacob March - Employee, Active: Today, Joined: January 2026
      // Coach Experience: OFF (Self-Led)
      coachId,
      localId: 'JacobMarch',
      firstName: 'Jacob',
      lastName: 'March',
      email: `coachled-member-${Date.now()}-2@testuser.ex-os.net`,
      createdAt: now,
      lastAppActivity: now,
      officeLocationId,
      shouldCreateSessions: false,
      memberExperience: MemberExperience.SelfLed,
      onboardingSurveys: {
        birthday: LocalDate.now().minusYears(30).toString(),
        height: 5.5, // 5'6"
        weight: 150,
        identity: 0, // Male
        focusArea: 0, // Fitness
        motivation: 3, // Nothing can derail me, I've got this
        barriers: [0, 2, 6], // Time, Family, No energy
        passions: [18, 12, 15], // Travel, Running, Self-Care
        experience: 2, // I've got a good routine
        movement1: 3, // 5-6 days active (5)
        movement2: 4, // Toes
        nutrition1: 2, // Sometimes
        nutrition2: 4, // 81 oz or more
        recovery1: 1, // 5-6 hrs
        recovery2: 1, // Rarely
        recovery3: 3, // Often
        mindset1: 3, // Often
        mindset2: 3, // Often
        mindset3: 2, // Sometimes
      },
    },
    {
      // Vishal Reddy - Non-binary, Active: 2 days ago, Joined: January 2026
      // Coach Experience: ON, Upcoming session: Yes, Pre-assessment: Assigned and Completed
      coachId,
      localId: 'VishalReddy',
      firstName: 'Vishal',
      lastName: 'Reddy',
      email: `coachled-member-${Date.now()}-3@testuser.ex-os.net`,
      createdAt: now.minusDays(2),
      lastAppActivity: now.minusDays(2),
      officeLocationId,
      shouldCreateSessions: true,
      memberExperience: MemberExperience.CoachLed,
      completeGameplanPreassessments: true,
      lowestScoringPQ: 'Awareness',
      note: {
        coachId,
        title: 'Initial 6.6.26',
        content: `<p>Discovery 7.6.26</p><p>Focus Area: Mental &amp; Emotional Wellbeing&nbsp;</p><p><br class="ProseMirror-trailingBreak"></p><p>Performance Quality: Awareness 17%, 63% Sleep, 66% Digestive &amp; Met. Fxn. 67% Force Prod, 67% Self Management, 67% Curiosity</p><p><br class="ProseMirror-trailingBreak"></p><p>Why:&nbsp; Stress taking away from personal life with kids at home.&nbsp;</p><p>Schedule: 6 AM Wake | 12 PM Bed Work: 8 AM - 5 PM + 60 min commute&nbsp;</p><p>Sleep: Phone Scroll/TV until bed&nbsp;</p><p>Training: Beginner, 1-2x/wk past few years. Yoga when he was younger.&nbsp;</p><p>No injuries</p><p>Nutrition: Eats decent, home-cooked family meals, Indian cuisine.</p><p><br class="ProseMirror-trailingBreak"></p><p>What He Wants</p><p><br class="ProseMirror-trailingBreak"></p><p>Energy &amp; Family Time&nbsp;</p><p>Improve bed time discipline (no phone)&nbsp;</p><p>Combat end-of-day fatigue</p><p>Stress Management Better speed-of-life management at work/home&nbsp;</p><p>Reintroduce yoga practice (remembered positive feelings)</p><p>Sustainable, small, short movement breaks or training onsite workout 2x/wk at FCM Work breaks, breathework&nbsp;</p><p>Home/weekend sessions (15-30 mins)</p>`,
      },
      onboardingSurveys: {
        birthday: LocalDate.now().minusYears(47).toString(),
        height: 5.75, // 5'9"
        weight: 170,
        identity: 2, // Non-binary
        focusArea: 2, // Mental & Emotional Well-being
        motivation: 1, // Ready but need help getting started
        barriers: [5], // Confidence
        passions: [7, 11], // Learning new things, Reading
        experience: 0, // I'm new to fitness
        movement1: 0, // No days
        movement2: 4, // Toes
        nutrition1: 4, // Always
        nutrition2: 2, // 41-60 oz
        recovery1: 2, // 7-8 hrs
        recovery2: 2, // Sometimes
        recovery3: 3, // Often
        mindset1: 3, // Often
        mindset2: 3, // Often
        mindset3: 4, // Always
      },
    },
    {
      // Janet Moon - Female, Active: Today, Joined: January 2026
      // Coach Experience: ON, Upcoming session: Yes, Pre-assessment: Assigned and not completed
      coachId,
      localId: 'JanetMoon',
      firstName: 'Janet',
      lastName: 'Moon',
      email: `coachled-member-${Date.now()}-4@testuser.ex-os.net`,
      createdAt: now,
      lastAppActivity: now,
      officeLocationId,
      shouldCreateSessions: true,
      memberExperience: MemberExperience.CoachLed,
      onboardingSurveys: {
        birthday: LocalDate.now().minusYears(35).toString(),
        height: 5.83, // 5'10"
        weight: 205,
        identity: 1, // Female
        focusArea: 3, // Longevity & Healthy Aging
        motivation: 1, // I'm ready, but need help getting started
        barriers: [4, 0, 3], // Work, time, pain or injury
        passions: [9, 18, 12, 15], // Music, travel, running, self-care
        experience: 1, // I go through phases
        movement1: 1, // 1-2 days
        movement2: 2, // Shins
        nutrition1: 3, // Often
        nutrition2: 5, // I don't know
        recovery1: 2, // 7-8 hrs
        recovery2: 2, // Sometimes
        recovery3: 3, // Often
        mindset1: 2, // Sometimes
        mindset2: 2, // Sometimes
        mindset3: 2, // Sometimes
      },
    },
    {
      // William Davidson - Male, Active: Today, Joined: December 2025
      // Coach Experience: ON, No upcoming session, Pre-assessment: Assigned and Completed
      coachId,
      localId: 'WilliamDavidson',
      firstName: 'William',
      lastName: 'Davidson',
      email: `coachled-member-${Date.now()}-5@testuser.ex-os.net`,
      createdAt: now.minusMonths(1).minusDays(14), // December 2025
      lastAppActivity: now,
      officeLocationId,
      shouldCreateSessions: false,
      memberExperience: MemberExperience.CoachLed,
      completeGameplanPreassessments: true,
      lowestScoringPQ: 'Body Composition',
      note: {
        coachId,
        title: 'Initial 4.9.26',
        content: `<p>4.9.26 Discovery Session</p><p>Focus Area: Weight Loss</p><p>PQ scores: 25% Body Comp, 66% Digestive &amp; Metabolic, 67% Force Prod, 67% Grit</p><p><br class="ProseMirror-trailingBreak"></p><p>Wants to lose 30 pounds</p><p>"Why": Can’t do activities he did in college (sports, hiking). Lack of motivation/energy; scared for the future.&nbsp;</p><p><br class="ProseMirror-trailingBreak"></p><p>Background: 45 years old, wife, no kids. Former college powerlifter &amp; rugby player.</p><p><br class="ProseMirror-trailingBreak"></p><p>Onsite: 3X/wk with FCM (40 min commute).&nbsp;</p><p><br class="ProseMirror-trailingBreak"></p><p>Current Routine &amp; Pain Schedule: Bedtime 11PM-12AM, Awake 7AM.&nbsp;</p><p><br class="ProseMirror-trailingBreak"></p><p>Nutrition: Oatmeal, snacks, campus/leftover lunch, dinner out, dessert.&nbsp;</p><p><br class="ProseMirror-trailingBreak"></p><p>Activity: Inconsistent; walks dog 2 days/wk.</p><p><br class="ProseMirror-trailingBreak"></p><p>Pain: Chronic dull low back pain (5+ years).&nbsp;</p><p><br class="ProseMirror-trailingBreak"></p><p>Gameplan Solutions:&nbsp;</p><p>1. Training FCM 3X/wk (45 min sessions). Open to Training plan, on-demand, or group fitness. Intermediate/advanced knowledge of training - hates cardio.&nbsp;</p><p>2. Nutrition &amp; Lifestyle Reduce poor snacks &amp; eating out (target 2-3X home meals).</p><p>3. Improve sleep (limit TV/Phone late at night).&nbsp;</p><ul><li><p>Not as willing to focus on this at this time</p></li></ul><p><br class="ProseMirror-trailingBreak"></p><p>Goal: return to hiking/pickleball with friends.</p>`,
      },
      onboardingSurveys: {
        birthday: LocalDate.now().minusYears(36).toString(),
        height: 5.83, // 5'10"
        weight: 205,
        identity: 0, // Male
        focusArea: 1, // Weight Loss
        motivation: 1, // I'm ready, but need help getting started
        barriers: [4, 0, 3], // Work, time, pain or injury
        passions: [9, 18, 12, 15], // Music, travel, running, self-care
        experience: 1, // I go through phases
        movement1: 1, // 1-2 days
        movement2: 2, // Shins
        nutrition1: 3, // Often
        nutrition2: 5, // I don't know
        recovery1: 2, // 7-8 hrs
        recovery2: 2, // Sometimes
        recovery3: 3, // Often
        mindset1: 2, // Sometimes
        mindset2: 2, // Sometimes
        mindset3: 2, // Sometimes
      },
    },
    {
      // Caitlyn Woo - ONBOARDING NOT COMPLETE
      coachId,
      localId: 'CaitlynWoo',
      firstName: 'Caitlyn',
      lastName: 'Woo',
      email: `coachled-member-${Date.now()}-6@testuser.ex-os.net`,
      createdAt: now,
      lastAppActivity: now,
      officeLocationId,
      shouldCreateSessions: false,
      skipOnboardingSurveys: true,
    },
  ];
  return members;
};
